import { useEffect, useState } from 'react';
import { getSectionWithArticles } from '../services/manual.service';
import type { ManualArticle, ManualSection } from '../lib/supabase.types';
import ManualLayout from '../components/ManualLayout';
import { ErrorDisplay } from '../components/ErrorDisplay';

interface ManualSectionPageProps {
  sectionId: string;
  language: 'ja' | 'en';
  onLanguageChange: (lang: 'ja' | 'en') => void;
}

export default function ManualSectionPage({ sectionId, language, onLanguageChange }: ManualSectionPageProps) {
  const [section, setSection] = useState<ManualSection | null>(null);
  const [articles, setArticles] = useState<ManualArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSection = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getSectionWithArticles(sectionId, language);
      setSection(result.section);
      setArticles(result.articles);
    } catch (err) {
      console.error('Failed to load section:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSection();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, language]);

  if (loading) {
    return (
      <ManualLayout language={language} onLanguageChange={onLanguageChange}>
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          {language === 'en' ? 'Loading...' : '読み込み中...'}
        </div>
      </ManualLayout>
    );
  }

  if (error) {
    return <ErrorDisplay error={error} />;
  }

  if (!section || articles.length === 0) {
    return (
      <ManualLayout language={language} onLanguageChange={onLanguageChange}>
        <div className="manual-not-found">
          <h2>{language === 'en' ? 'Section not found' : 'セクションが見つかりません'}</h2>
          <p>{language === 'en'
            ? 'The specified section does not exist or has no articles.'
            : '指定されたセクションは存在しないか、記事がありません。'}</p>
          <button
            className="manual-back-button"
            onClick={() => window.location.hash = '#/manual'}
          >
            {language === 'en' ? 'Back to Manual' : 'マニュアルTOPへ戻る'}
          </button>
        </div>
      </ManualLayout>
    );
  }

  const handleNavigate = (slug: string) => {
    window.location.hash = `#/manual/article/${slug}`;
  };

  return (
    <ManualLayout currentPath={`/manual/${sectionId}`} language={language} onLanguageChange={onLanguageChange}>
      <div className="manual-section">
        <nav className="manual-breadcrumb">
          <button onClick={() => window.location.hash = '#/manual'}>Manual</button>
          <span className="manual-breadcrumb-separator">›</span>
          <span>{section.title}</span>
        </nav>

        <h1 className="manual-section-title">{section.title}</h1>

        <div className="manual-section-items">
          {articles.map((article) => (
            <button
              key={article.id}
              className="manual-section-item-card"
              onClick={() => handleNavigate(article.slug)}
            >
              <h2 className="manual-section-item-title">{article.title}</h2>
              {article.description && (
                <p className="manual-section-item-summary">{article.description}</p>
              )}
            </button>
          ))}
        </div>
      </div>
    </ManualLayout>
  );
}
